import { Box, IconButton, Stack, Typography } from '@mui/material';
import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import { Accept, useDropzone } from 'react-dropzone';
import { Trash01, UploadCloud01 } from 'untitledui-js-base';

const baseStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '40px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fff',
  color: 'text.main',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  cursor: 'pointer',
  height: '100%'
};

const focusedStyle: CSSProperties = {
  borderColor: '#2196f3'
};

const acceptStyle: CSSProperties = {
  borderColor: '#00e676'
};

const rejectStyle: CSSProperties = {
  borderColor: '#ff1744'
};

const thumbsContainer = {
  display: 'flex',
  gap: '10px',
  flexWrap: 'wrap',
  marginTop: '1rem'
};

const img = {
  display: 'block',
  width: 'auto',
  height: '100%'
};

interface CustomDropzoneProps extends Omit<React.HTMLProps<HTMLInputElement>, 'accept'> {
  accept?: Accept;
  showFiles?: boolean;
  maxFiles?: number;
  onLoadedFiles?: (files: File[]) => void;
  title?: string;
}

export const CustomDropzone: React.FC<CustomDropzoneProps> = ({
  accept = { 'image/*': [] },
  showFiles,
  maxFiles = 1,
  onLoadedFiles,
  title = 'Nahrajte soubor(y)'
}) => {
  const [files, setFiles] = useState<(File & { preview: string })[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    accept,
    maxFiles,
    multiple: maxFiles > 1,
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        setError(`Můžete nahrát maximálně ${maxFiles} soubor${maxFiles > 1 ? 'y' : ''}.`);
        return;
      }

      setError(null);

      const mappedFiles = acceptedFiles.map((file) => Object.assign(file, { preview: URL.createObjectURL(file) }));

      setFiles((prevImages) => [...prevImages, ...mappedFiles]);

      onLoadedFiles?.(mappedFiles);
    }
  });

  const handleRemoveFile = (fileToRemove: File) => {
    setFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter((file) => file !== fileToRemove);
      onLoadedFiles?.(updatedFiles);
      return updatedFiles;
    });
  };

  useEffect(() => {
    return () => files.forEach((file) => URL.revokeObjectURL(file.preview));
  }, [files]);

  const style: CSSProperties = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {})
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  const thumbs = files.map((file) => (
    <Box
      sx={{
        display: 'flex',
        gap: '10px',
        justifyContent: 'center',
        borderRadius: 2,
        border: '1px solid #eaeaea',
        width: 150,
        height: 150,
        position: 'relative'
      }}
      key={file.name}
    >
      <IconButton
        color="error"
        onClick={() => handleRemoveFile(file)}
        size="small"
        sx={{ position: 'absolute', right: '5px', top: '5px', zIndex: 99 }}
      >
        <Trash01 />
      </IconButton>
      <Box
        sx={{
          display: 'flex',
          minWidth: 0,
          overflow: 'hidden'
        }}
      >
        <img alt={file.name} src={file.preview} style={img} onLoad={() => URL.revokeObjectURL(file.preview)} />
      </Box>
    </Box>
  ));

  useEffect(() => {
    return () => files.forEach((file) => URL.revokeObjectURL(file.preview));
  }, [files]);

  return (
    <>
      <Box {...getRootProps({ style })}>
        <input {...getInputProps()} />
        <Stack gap={2} alignItems="center" justifyContent="center">
          <Stack
            justifyContent="center"
            sx={{
              paddingY: '.8rem',
              paddingX: '2rem',
              backgroundColor: '#F1F1F5',
              borderRadius: '3px'
            }}
          >
            <UploadCloud01 />
          </Stack>
          <Stack alignItems="center">
            <Typography fontWeight="600">{title}</Typography>
            <Typography color="gray" textAlign="center">
              Kliknutím nahrajete soubory
            </Typography>
          </Stack>
        </Stack>
      </Box>
      {error && (
        <Typography color="error" sx={{ mt: 2 }} variant="caption">
          {error}
        </Typography>
      )}
      {showFiles && thumbs.length > 0 && (
        <Box component="aside" sx={thumbsContainer}>
          {thumbs}
        </Box>
      )}
    </>
  );
};
